import React, { useState, useEffect } from 'react';

/**
 * BackToTop Component
 * Features:
 * - Fixed circular button with white liquid bubble animation.
 * - Responsive sizing using clamp() for mobile/desktop.
 * - Scroll-to-top functionality with smooth behavior.
 * - Fade-in/out visibility based on scroll position.
 * - Multi-wave layered liquid effect with foam bubbles.
 */

const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [fillLevel, setFillLevel] = useState(60);

  useEffect(() => {
    const toggleVisibility = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = maxScroll > 0 ? (scrolled / maxScroll) * 100 : 0;

      if (scrolled > 300) {
        setIsVisible(true);
        // Fill level inversely tied to how far you've scrolled
        // At top (scroll=300): nearly empty; at bottom: nearly full
        const fill = Math.min(85, Math.max(15, scrollPercent));
        setFillLevel(100 - fill + 15);
      } else {
        setIsVisible(false);
        setFillLevel(60);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400&display=swap');

        .btt-wrap {
          position: fixed;
          bottom: clamp(90px, 20vh, 68px);
          right: clamp(16px, 2vw, 32px);
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px) scale(0.85);
          transition: opacity 0.45s cubic-bezier(.4,0,.2,1),
                      visibility 0.45s,
                      transform 0.45s cubic-bezier(.4,0,.2,1);
                      
        }

        .btt-wrap.visible {
          opacity: 1;
          visibility: visible;
          transform: translateY(0) scale(1);
        }

        /* Outer glow ring */
        .btt-ring {
          width: clamp(52px, 6vw, 64px);
          height: clamp(52px, 6vw, 64px);
          border-radius: 50%;
          padding: 3px;
          background: conic-gradient(
            rgba(255,255,255,0.9) 0%,
            rgba(160,220,255,0.6) 40%,
            rgba(80,180,255,0.3) 70%,
            rgba(255,255,255,0.9) 100%
          );
          animation: spin-ring 6s linear infinite;
          box-shadow:
            0 0 18px rgba(100, 180, 255, 0.45),
            0 0 40px rgba(100, 180, 255, 0.2),
            inset 0 0 12px rgba(255, 255, 255, 0.15);
          cursor: pointer;
          transition: box-shadow 0.35s ease, transform 0.2s ease;
        }

        .btt-ring:hover {
          box-shadow:
            0 0 28px rgba(100, 200, 255, 0.7),
            0 0 60px rgba(100, 200, 255, 0.35),
            inset 0 0 18px rgba(255, 255, 255, 0.3);
        }

        .btt-ring:active {
          transform: scale(0.93);
        }

        @keyframes spin-ring {
          to { transform: rotate(360deg); }
        }

        /* Main button */
        .btt-btn {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: none;
          background: linear-gradient(160deg, #1a6fb5 0%, #0a4a8a 60%, #062f60 100%);
          overflow: hidden;
          position: relative;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: inset 0 2px 8px rgba(255,255,255,0.18), inset 0 -3px 8px rgba(0,0,0,0.3);
        }

        /* ---- WAVE LAYERS ---- */
        .wave-container {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          overflow: hidden;
          pointer-events: none;
        }

        /* Each wave is a wide pseudo-oval div rotating */
        .wave {
          position: absolute;
          left: -60%;
          width: 220%;
          background: rgba(255, 255, 255, 0.22);
          border-radius: 42%;
          transform-origin: 50% 50%;
        }

        .wave-1 {
          height: 200%;
          bottom: calc(var(--fill, 60) * 1% - 100%);
          animation: wave-rotate 4.5s linear infinite;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 44%;
        }

        .wave-2 {
          height: 200%;
          bottom: calc(var(--fill, 60) * 1% - 102%);
          animation: wave-rotate 6s linear infinite reverse;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 38%;
        }

        .wave-3 {
          height: 200%;
          bottom: calc(var(--fill, 60) * 1% - 105%);
          animation: wave-rotate 8s linear infinite;
          background: rgba(200, 235, 255, 0.12);
          border-radius: 45%;
        }

        /* Hover: waves rise */
        .btt-btn:hover .wave-1 {
          bottom: calc(var(--fill, 60) * 1% - 90%);
          background: rgba(255, 255, 255, 0.45);
        }
        .btt-btn:hover .wave-2 {
          bottom: calc(var(--fill, 60) * 1% - 92%);
          background: rgba(255, 255, 255, 0.3);
        }
        .btt-btn:hover .wave-3 {
          bottom: calc(var(--fill, 60) * 1% - 95%);
        }

        .wave-1, .wave-2, .wave-3 {
          transition: bottom 0.55s cubic-bezier(.4,0,.2,1),
                      background 0.35s ease;
        }

        @keyframes wave-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* ---- FOAM BUBBLES ---- */
        .bubbles {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
          border-radius: 50%;
        }

        .bubble {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.55);
          box-shadow: 0 0 4px rgba(255,255,255,0.6), inset 0 1px 2px rgba(255,255,255,0.9);
          animation: rise linear infinite;
          bottom: -10px;
        }

        .bubble:nth-child(1) { width: 5px; height: 5px; left: 20%; animation-duration: 3.2s; animation-delay: 0s; }
        .bubble:nth-child(2) { width: 3px; height: 3px; left: 50%; animation-duration: 2.5s; animation-delay: 0.8s; }
        .bubble:nth-child(3) { width: 4px; height: 4px; left: 70%; animation-duration: 4s; animation-delay: 1.5s; }
        .bubble:nth-child(4) { width: 2px; height: 2px; left: 35%; animation-duration: 3.7s; animation-delay: 2.2s; }
        .bubble:nth-child(5) { width: 5px; height: 5px; left: 80%; animation-duration: 2.9s; animation-delay: 0.5s; }
        .bubble:nth-child(6) { width: 3px; height: 3px; left: 10%; animation-duration: 3.5s; animation-delay: 1.8s; }

        @keyframes rise {
          0%   { transform: translateY(0) scale(1); opacity: 0.7; }
          70%  { opacity: 0.5; }
          100% { transform: translateY(-65px) scale(0.4); opacity: 0; }
        }

        /* ---- SURFACE SHIMMER ---- */
        .shimmer {
          position: absolute;
          top: 14%;
          left: 15%;
          width: 35%;
          height: 18%;
          background: linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 100%);
          border-radius: 50%;
          transform: rotate(-25deg);
          pointer-events: none;
          z-index: 4;
          animation: shimmer-pulse 3.5s ease-in-out infinite;
        }

        @keyframes shimmer-pulse {
          0%, 100% { opacity: 0.7; }
          50%       { opacity: 1; }
        }

        /* ---- ARROW ICON ---- */
        .btt-icon {
          position: relative;
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          transition: transform 0.3s cubic-bezier(.4,0,.2,1);
          color: rgba(255, 255, 255, 0.95);
          filter: drop-shadow(0 1px 3px rgba(0,0,0,0.35));
        }

        .btt-btn:hover .btt-icon {
          transform: translateY(-3px);
          color: #fff;
          filter: drop-shadow(0 2px 6px rgba(0,0,0,0.5));
        }

        /* Media queries */
        @media (max-width: 768px) {
          .btt-ring {
            width: clamp(44px, 5vw, 56px);
            height: clamp(44px, 5vw, 56px);
          }
        }
        @media (max-width: 480px) {
          .btt-ring {
            width: 48px;
            height: 48px;
          }
          .btt-wrap {
            bottom: 90px;
            right: 14px;
          }
        }
      `}</style>

      <div className={`btt-wrap ${isVisible ? 'visible' : ''}`}>
        <div className="btt-ring" onClick={scrollToTop} role="button" aria-label="Scroll to top">
          <button className="btt-btn" aria-label="Back to top">

            {/* Wave layers — fill level set via CSS var */}
            <div className="wave-container">
              <div
                className="wave wave-1"
                style={{ '--fill': fillLevel }}
              />
              <div
                className="wave wave-2"
                style={{ '--fill': fillLevel }}
              />
              <div
                className="wave wave-3"
                style={{ '--fill': fillLevel }}
              />
            </div>

            {/* Rising foam bubbles */}
            <div className="bubbles">
              <span className="bubble" />
              <span className="bubble" />
              <span className="bubble" />
              <span className="bubble" />
              <span className="bubble" />
              <span className="bubble" />
            </div>

            {/* Glass shimmer highlight */}
            <div className="shimmer" />

            {/* Arrow icon */}
            <span className="btt-icon">
              <svg
                width="1.1em"
                height="1.1em"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </span>

          </button>
        </div>
      </div>
    </>
  );
};

export default BackToTop;